import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { departmentsApi, employees, hireOrders, positionsApi } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Українське відмінювання «днів»: 1 день / 2-4 дні / 5+ днів (з урахуванням 11-14).
function pluralDays(n: number): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'днів';
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дні';
  return 'днів';
}

export default function HireOrderPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['hire-order', id ?? ''],
    queryFn: () => hireOrders.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const empQ = useQuery({
    queryKey: ['employee-full', docQ.data?.employee_id ?? ''],
    queryFn: () => employees.get(docQ.data!.employee_id),
    enabled: !!docQ.data,
  });
  const posQ = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list({ limit: 500 }) });
  const depQ = useQuery({
    queryKey: ['departments', docQ.data?.organization_id ?? ''],
    queryFn: () => departmentsApi.list({ organization_id: docQ.data!.organization_id, limit: 500 }),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data || !empQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const d = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === d.organization_id);
  const posName = d.position_id ? posQ.data?.items.find((p) => p.id === d.position_id)?.name : null;
  const depName = d.department_id ? depQ.data?.items.find((p) => p.id === d.department_id)?.name : null;

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt',
      maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)', lineHeight: 1.4,
    }}>
      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.hire_orders',
        documentId: d.id,
        filename: `Наказ про прийом Т-1 ${d.document_number ?? ''}`.trim(),
        orgName: org?.name ?? null,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && <div style={{ fontSize: '9pt' }}>{partyCodesText(org)}</div>}
      </div>
      <div style={{ textAlign: 'right', fontSize: '9pt', marginBottom: '0.5rem' }}>
        Типова форма № П-1<br/>
        затв. наказом Держкомстату України
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '1rem 0 0.3rem' }}>
        НАКАЗ № {d.document_number}
      </h2>
      <p style={{ textAlign: 'center', margin: 0 }}>
        від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </p>
      <p style={{ textAlign: 'center', margin: '0.5rem 0', fontWeight: 600 }}>
        про прийом на роботу
      </p>

      <p style={{ marginTop: '1.5rem' }}>
        Прийняти на роботу з <strong>{new Date(d.hire_date).toLocaleDateString('uk-UA')}</strong>:
      </p>

      <table style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse', border: '1px solid #000' }}>
        <tbody>
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000', width: '35%' }}>
              <strong>Прізвище, ім'я, по батькові:</strong>
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {empQ.data.full_name ?? '—'}
            </td>
          </tr>
          {empQ.data.inn && (
            <tr>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
                <strong>ІПН:</strong>
              </td>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}>{empQ.data.inn}</td>
            </tr>
          )}
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              <strong>Табельний номер:</strong>
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {empQ.data.code ?? '—'}
            </td>
          </tr>
          {depName && (
            <tr>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
                <strong>Підрозділ:</strong>
              </td>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}>{depName}</td>
            </tr>
          )}
          {posName && (
            <tr>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
                <strong>Посада:</strong>
              </td>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}>{posName}</td>
            </tr>
          )}
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              <strong>Тарифна ставка (оклад):</strong>
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {fmt2(d.salary_amount)} грн на місяць
            </td>
          </tr>
          {(() => {
            // Fallback: імпортні накази з 1С несуть probation_months → × 30 днів.
            const days = d.probation_days ?? (d.probation_months != null ? d.probation_months * 30 : null);
            return days != null ? (
              <tr>
                <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
                  <strong>Випробування:</strong>
                </td>
                <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
                  {days} {pluralDays(days)}
                </td>
              </tr>
            ) : null;
          })()}
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              <strong>Умови роботи:</strong>
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {empQ.data.is_main_workplace ? 'основне місце роботи' : 'сумісництво'}
            </td>
          </tr>
        </tbody>
      </table>

      {d.contract_basis && (
        <p style={{ marginTop: '1rem' }}>
          <strong>Підстава:</strong> {d.contract_basis}
        </p>
      )}

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div>З наказом ознайомлений:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                (підпис працівника, дата)
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
